import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDays, fmtH, PART_LABEL } from "@/lib/people";
import { toast } from "sonner";

const GOLD = "bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]";

function Preview({ p, error, type }) {
  if (error) return <p className="flex items-start gap-1.5 rounded-lg bg-[#C95555]/10 px-3 py-2.5 text-sm text-[#C95555]" data-testid="leave-preview-error"><Icons.CircleAlert size={15} className="mt-px shrink-0" /> {error}</p>;
  if (!p) return null;
  return (
    <div className="rounded-lg bg-[#F4F6F9] px-3.5 py-3 text-sm" data-testid="leave-preview">
      <div className="font-semibold text-[#0B1729]">Uses {fmtDays(p.hours, p.day_hours)} <span className="font-normal text-[#1E2D44]/55">({fmtH(p.hours)} from your work pattern)</span></div>
      {p.available_after != null && <div className="mt-0.5 text-[#1E2D44]/70">{type?.name} left afterwards: <b>{fmtDays(p.available_after, p.day_hours)}</b> in {p.year_label}</div>}
      <div className="mt-0.5 text-xs text-[#1E2D44]/55">{p.requires_approval ? `Goes to ${p.approver_name || "an approver"} for approval.` : "Recorded straight away — your approver is told."}</div>
    </div>
  );
}

export function LeaveRequestDialog({ orgId, types, onClose, onSaved }) {
  const [f, setF] = useState({ leave_type_id: types[0]?.id || "", span: "one", start_date: "", end_date: "", part: "full", hours: "", reason: "" });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const type = types.find((t) => t.id === f.leave_type_id);
  const body = {
    leave_type_id: f.leave_type_id, start_date: f.start_date, end_date: f.span === "one" ? f.start_date : f.end_date,
    part: f.span === "one" ? f.part : "full", hours: f.part === "hours" && f.span === "one" ? Number(f.hours) || null : null,
  };
  const ready = body.leave_type_id && body.start_date && body.end_date && (body.part !== "hours" || body.hours);
  const key = JSON.stringify(body);

  useEffect(() => {
    setPreview(null); setError("");
    if (!ready) return undefined;
    const t = setTimeout(() => {
      api.post(`/workspace/leave/preview?org_id=${orgId}`, body).then((r) => setPreview(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
    }, 300);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!ready) return toast.error("Choose the leave type and dates");
    setSaving(true);
    try {
      const { data } = await api.post(`/workspace/leave/requests?org_id=${orgId}`, { ...body, reason: f.reason || null });
      toast.success(data.status === "approved" ? "Absence recorded" : "Request sent for approval");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="leave-request-dialog">
        <DialogHeader>
          <DialogTitle>Request time off</DialogTitle>
          <DialogDescription>Hours are worked out from your work pattern. Public holidays and days you don't work aren't counted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Type</Label>
            <Select value={f.leave_type_id} onValueChange={(v) => set("leave_type_id", v)}>
              <SelectTrigger className="mt-1" data-testid="leave-type-select"><SelectValue placeholder="Choose a type" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            {type?.sensitive && <p className="mt-1 flex items-center gap-1 text-xs text-[#1E2D44]/50"><Icons.Lock size={11} /> Your manager sees only "Absent". The type and reason are visible to you and people administrators.</p>}
          </div>
          <div className="flex gap-1.5 rounded-lg bg-[#F4F6F9] p-1">
            {[["one", "One day"], ["many", "Several days"]].map(([k, l]) => (
              <button key={k} onClick={() => set("span", k)} data-testid={`leave-span-${k}`}
                className={`flex-1 rounded-md py-1.5 text-sm font-medium transition-colors ${f.span === k ? "bg-white text-[#0B1729] shadow-sm" : "text-[#1E2D44]/55"}`}>{l}</button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>{f.span === "one" ? "Date" : "From"}</Label><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1" data-testid="leave-start" /></div>
            {f.span === "many" ? (
              <div><Label>To</Label><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1" data-testid="leave-end" /></div>
            ) : (
              <div><Label>How much</Label>
                <Select value={f.part} onValueChange={(v) => set("part", v)}>
                  <SelectTrigger className="mt-1" data-testid="leave-part"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PART_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          {f.span === "one" && f.part === "hours" && (
            <div><Label>Hours</Label><Input type="number" min="0.25" step="0.25" value={f.hours} onChange={(e) => set("hours", e.target.value)} className="mt-1 w-32" data-testid="leave-hours" /></div>
          )}
          <div><Label>Note (optional)</Label><Textarea rows={2} value={f.reason} onChange={(e) => set("reason", e.target.value)} className="mt-1" data-testid="leave-reason" /></div>
          <Preview p={preview} error={error} type={type} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !!error || !preview} className={GOLD} data-testid="leave-submit-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : type?.requires_approval === false ? "Record absence" : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
